// ============================================================================
//  WEBINAR — configuración en un ÚNICO sitio
// ----------------------------------------------------------------------------
//  Rellena estos campos cuando tengas el evento creado en Meetup. Este archivo
//  es la única fuente de verdad: la barra superior (todo el sitio) y la sección
//  de la Home leen de aquí.
//
//  Para OCULTAR el aviso (barra + sección) de golpe:
//     - pon  enabled: false   (o deja  meetupUrl: "")
//
//  No hace falta que te acuerdes de quitarlo: se oculta solo automáticamente
//  unas horas después de la hora de inicio (ver startsAt / GRACE_HOURS).
// ============================================================================

// Grupo de Meetup de la comunidad.
export const MEETUP_GROUP_URL =
  "https://www.meetup.com/ia-para-agencias-freelancers-y-consultores";

// Evento concreto del webinar (destino del botón "Apúntate en Meetup").
// Si en algún momento no hay evento activo, apunta meetupUrl a MEETUP_GROUP_URL.
const MEETUP_EVENT_URL =
  "https://www.meetup.com/ia-para-agencias-freelancers-y-consultores/events/316211287/";

export const webinar = {
  // Pon false para no mostrar nada.
  enabled: true,

  // Destino del botón (evento concreto de Meetup, o el grupo). Si lo dejas
  // vacío ("") no se muestra el aviso.
  meetupUrl: MEETUP_EVENT_URL,

  // ┌──────────────────────────────────────────────────────────────────────┐
  // │  FECHA DEL WEBINAR  →  se escribe AQUÍ (es lo que sale en barra/sección)│
  // └──────────────────────────────────────────────────────────────────────┘
  // Formato ISO 8601 CON zona horaria:
  //   +02:00 = hora de Madrid en verano (CEST) · +01:00 = invierno (CET)
  // Webinar: martes 15 de septiembre de 2026, 18:00 CEST (~90 min + Q&A).
  startsAt: "2026-09-15T18:00:00+02:00",

  // Duración en minutos (para el endDate de los datos estructurados de Google).
  durationMinutes: 120,

  // Título / tema del webinar, por idioma.
  title: {
    es: "IA aplicada: convierte procesos en servicios para tus clientes",
    en: "Applied AI: turn processes into services for your clients",
  },

  // Frase corta de apoyo (subtítulo de la sección), por idioma.
  tagline: {
    es: "Taller online y en directo: identifica procesos automatizables con IA y conviértelos en servicios escalables y repetibles para tus clientes.",
    en: "Live online workshop: identify processes you can automate with AI and turn them into scalable, repeatable services for your clients.",
  },

  // (Opcional) Texto de fecha escrito a mano. Si lo dejas vacío se calcula solo
  // desde startsAt en el idioma del visitante.
  dateLabelOverride: {
    es: "",
    en: "",
  },
};

// Zona horaria en la que se muestra la fecha al visitante (la del webinar).
const WEBINAR_TIME_ZONE = "Europe/Madrid";

// Horas que sigue visible el aviso después de la hora de inicio antes de ocultarse solo.
const GRACE_HOURS = 3;

const pickLang = (lang) =>
  String(lang || "es").toLowerCase().startsWith("en") ? "en" : "es";

const parseStart = () => {
  if (!webinar.startsAt) return null;
  const d = new Date(webinar.startsAt);
  return Number.isNaN(d.getTime()) ? null : d;
};

// ¿Hay un webinar con fecha real? Sólo entonces publicamos los datos
// estructurados de evento en Google (con un grupo sin fecha no procede).
export const isWebinarConfigured = () =>
  Boolean(webinar.meetupUrl) && Boolean(parseStart());

// ¿Hay que mostrar el aviso ahora mismo? (independiente del descarte del usuario)
export const isWebinarVisible = () => {
  if (!webinar.enabled || !webinar.meetupUrl) return false;
  const start = parseStart();
  if (!start) return true; // sin fecha válida -> lo mostramos igualmente
  return Date.now() < start.getTime() + GRACE_HOURS * 60 * 60 * 1000;
};

export const getWebinarTitle = (lang) =>
  webinar.title[pickLang(lang)] || webinar.title.es;

export const getWebinarTagline = (lang) =>
  webinar.tagline[pickLang(lang)] || webinar.tagline.es;

// Fecha formateada legible según idioma (o el override si lo has escrito a mano).
export const getWebinarDateLabel = (lang) => {
  const code = pickLang(lang);
  const override = webinar.dateLabelOverride[code];
  if (override) return override;

  const start = parseStart();
  if (!start) return "";

  try {
    return new Intl.DateTimeFormat(code === "en" ? "en-GB" : "es-ES", {
      weekday: "long",
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: WEBINAR_TIME_ZONE,
      timeZoneName: "short",
    }).format(start);
  } catch (e) {
    return start.toISOString();
  }
};

// Fin del evento en ISO (para el schema Event). null si no hay inicio válido.
export const getWebinarEndISO = () => {
  const start = parseStart();
  if (!start) return null;
  return new Date(
    start.getTime() + (webinar.durationMinutes || 60) * 60000
  ).toISOString();
};
